import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
	ApplicationConfig,
	EnvironmentProviders,
	ErrorHandler,
	inject,
	isDevMode,
	provideAppInitializer,
	Provider,
	provideZonelessChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
	PreloadAllModules,
	provideRouter,
	RouteReuseStrategy,
	withPreloading,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { AlertService } from '@context/frontend/alert';
import {
	API_URL,
	APP_VERSION,
	EXTERNAL_VIEWER_BASE_URL,
	ORGANIZATION_ID,
	provideContextTaiga,
	provideFirebase,
	SUPPORT_EMAIL,
	USER,
} from '@context/frontend/common';
import { AppRouteReuseStrategy } from '@context/frontend/navigation';
import { ALGOLIA } from '@context/frontend/search';
import { authBearerInterceptor } from '@context/frontend/user';
import { version } from '@context/package';
import { getExternalViewerBaseUrl } from '@context/shared/types/common';
import { User } from '@context/shared/types/user';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';
import {
	TUI_BUTTON_OPTIONS,
	tuiDateFormatProvider,
	tuiScrollbarOptionsProvider,
} from '@taiga-ui/core';
import { provideQuillConfig } from 'ngx-quill/config';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import { TranslocoHttpLoader } from './transloco-loader';

type Providers = (Provider | EnvironmentProviders)[];
type WindowNavigator = Window & { navigator: { standalone: boolean } };

/**
 * Determines if we are viewing the app as a PWA/Native App
 */
const isInStandaloneMode = () =>
	window.matchMedia('(display-mode: standalone)').matches ||
	(window as unknown as WindowNavigator).navigator.standalone ||
	document.referrer.includes('android-app://');

// We only want to add sentry if we are in production
const SentryProviders: Providers = environment.production
	? [
			{
				provide: ErrorHandler,
				useValue: Sentry.createErrorHandler({ showDialog: false }),
			},
		]
	: [];

const FirebaseProviders: Providers = environment.firebase
	? [provideFirebase(environment.firebase, environment.emulators)]
	: [];

export const appConfig: ApplicationConfig = {
	providers: [
		provideAnimations(),
		provideZonelessChangeDetection(),
		provideRouter(appRoutes, withPreloading(PreloadAllModules)),
		{ provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
		...SentryProviders,
		...FirebaseProviders,
		provideContextTaiga(),
		AlertService,
		tuiScrollbarOptionsProvider({ mode: 'hover' }),
		tuiDateFormatProvider({ mode: 'mm/dd/yyyy', separator: '/' }),
		provideHttpClient(withInterceptors([authBearerInterceptor])),
		provideTransloco({
			config: {
				availableLangs: [
					'en',
					// 'es'
				],
				defaultLang: 'en',
				// Remove this option if your application doesn't support changing language in runtime.
				reRenderOnLangChange: true,
				prodMode: !isDevMode(),
			},
			loader: TranslocoHttpLoader,
		}),
		{ provide: SUPPORT_EMAIL, useValue: environment.supportEmail },
		{ provide: API_URL, useValue: environment.apiUrl },
		{
			provide: EXTERNAL_VIEWER_BASE_URL,
			useFactory: () => getExternalViewerBaseUrl(environment),
		},
		{ provide: ALGOLIA, useValue: environment.algolia },
		{ provide: APP_VERSION, useValue: version },
		{ provide: USER, useValue: new BehaviorSubject<User | null>(null) },
		{
			provide: ORGANIZATION_ID,
			useValue: new BehaviorSubject<string | null>(null),
		},
		{
			provide: TUI_BUTTON_OPTIONS,
			useValue: { appearance: 'primary', size: 'm' },
		},
		provideServiceWorker('ngsw-worker.js', {
			enabled: isInStandaloneMode(),
			registrationStrategy: 'registerWhenStable:30000',
		}),
		provideAppInitializer(() => {
			const initializerFn = appInitializerFactory(
				inject(TranslocoService),
			);
			return initializerFn();
		}),
		provideQuillConfig({
			modules: {
				toolbar: [
					['bold', 'italic', 'underline', 'strike'],
					[{ list: 'ordered' }, { list: 'bullet' }],
					['link'],
					['clean'],
				],
			},
		}),
	],
};

/**
 * We want to load in the translations before any pages load to avoid
 * missing translation issues
 */
const BOOT_TRANSLATION_TIMEOUT_MS = 8000;

export function appInitializerFactory(transloco: TranslocoService) {
	return () => {
		transloco.setDefaultLang('en');
		// Don't let a stalled/blocked translation request hang bootstrap forever
		// (flaky mobile networks, or the PWA cache missing the i18n asset). The load
		// keeps running in the background and the UI re-renders once it resolves.
		return Promise.race([
			firstValueFrom(transloco.selectTranslation('en')),
			new Promise((resolve) =>
				setTimeout(resolve, BOOT_TRANSLATION_TIMEOUT_MS),
			),
		]);
	};
}
